/**
 * Стиль базовой подложки поверх схемы OpenMapTiles (её выдаёт planetiler).
 *
 * Подложка намеренно приглушённая и низкоконтрастная: поверх неё лежат
 * полупрозрачные полигоны функциональных зон, и любая яркая деталь подложки
 * начинает конкурировать с цветом зоны, то есть мешает читать документ.
 * Дороги и подписи остаются различимыми, всё остальное уходит в фон.
 */

import type {
  LayerSpecification,
  RasterSourceSpecification,
  StyleSpecification,
  VectorSourceSpecification,
} from "@maplibre/maplibre-gl-style-spec";
import type { FontStacks } from "./fonts";

export type BasemapTheme = "light" | "dark" | "none";

interface Palette {
  background: string;
  water: string;
  waterLabel: string;
  wood: string;
  grass: string;
  landuseResidential: string;
  landuseIndustrial: string;
  building: string;
  buildingOutline: string;
  roadMajor: string;
  roadMinor: string;
  roadCasing: string;
  rail: string;
  boundary: string;
  label: string;
  labelHalo: string;
}

const PALETTES: Record<"light" | "dark", Palette> = {
  light: {
    background: "#f2efe9",
    water: "#b3d0e3",
    waterLabel: "#4a6f85",
    wood: "#d8e3d0",
    grass: "#e2ead9",
    landuseResidential: "#ebe7e0",
    landuseIndustrial: "#e6e3e3",
    building: "#d8d1c5",
    buildingOutline: "#bdb4a6",
    roadMajor: "#ffffff",
    roadMinor: "#ffffff",
    roadCasing: "#a9a095",
    rail: "#9d968c",
    boundary: "#8a8178",
    label: "#3a3631",
    labelHalo: "#f7f5f2",
  },
  dark: {
    background: "#1b1d21",
    water: "#22303a",
    waterLabel: "#6e8b9b",
    wood: "#1f2622",
    grass: "#202722",
    landuseResidential: "#212429",
    landuseIndustrial: "#23252a",
    building: "#272a30",
    buildingOutline: "#2f333a",
    roadMajor: "#3a3f47",
    roadMinor: "#2e323a",
    roadCasing: "#22252a",
    rail: "#343841",
    boundary: "#4c525b",
    label: "#c8ccd2",
    labelHalo: "#15171a",
  },
};

export interface BasemapStyleOptions {
  /** Абсолютный URL .pmtiles с подложкой. */
  tileUrl: string;
  glyphsUrl: string;
  attribution: string;
  fonts: FontStacks;
  theme: BasemapTheme;
  minzoom: number;
  maxzoom: number;
  /** [minLon, minLat, maxLon, maxLat] */
  bounds: [number, number, number, number];
}

const SOURCE_ID = "basemap";

export function createBasemapStyle(options: BasemapStyleOptions): StyleSpecification {
  const { tileUrl, glyphsUrl, attribution, fonts, theme, minzoom, maxzoom, bounds } = options;
  const palette = PALETTES[theme === "dark" ? "dark" : "light"];

  const style: StyleSpecification = {
    version: 8,
    glyphs: glyphsUrl,
    sources: {},
    layers: [
      {
        id: "background",
        type: "background",
        paint: { "background-color": palette.background },
      },
    ],
  };

  // Режим «без подложки»: остаётся только фон, чтобы зоны генплана читались
  // как чистый чертёж. Источник подложки при этом не подключается вовсе —
  // лишних сетевых запросов не будет.
  if (theme === "none") {
    return style;
  }

  style.sources[SOURCE_ID] = vectorSource(tileUrl, attribution, minzoom, maxzoom, bounds);

  style.layers.push(...basemapLayers(palette, fonts));
  return style;
}

/**
 * Источник векторных тайлов из одного PMTiles-архива.
 *
 * Здесь намеренно НЕ используется форма `url: "pmtiles://..."`. С ней MapLibre
 * запрашивает у протокола TileJSON, получает его — и на этом останавливается,
 * не запрашивая ни одного тайла (maplibre-gl-js#8186). Карта при этом молчит:
 * ни ошибки, ни события load.
 *
 * Всё, что было бы в TileJSON — зумы и границы, — и так лежит в manifest.json,
 * который конвейер собирает из заголовка архива. Поэтому источник описывается
 * явно: и баг обойдён, и один сетевой запрос сэкономлен.
 */
export function vectorSource(
  tileUrl: string,
  attribution: string,
  minzoom: number,
  maxzoom: number,
  bounds: [number, number, number, number],
): VectorSourceSpecification {
  return {
    type: "vector",
    tiles: [`pmtiles://${tileUrl}/{z}/{x}/{y}`],
    minzoom,
    maxzoom,
    bounds,
    attribution,
  };
}

/**
 * Источник растровых тайлов.
 *
 * Нужен для листов генплана: ФГИС ТП публикует их картинками, а не векторами.
 * Кликов по зонам и атрибутов у такого слоя нет и быть не может — есть только
 * сам чертёж, привязанный к координатам.
 */
export function rasterSource(
  tileUrl: string,
  attribution: string,
  minzoom: number,
  maxzoom: number,
  bounds: [number, number, number, number],
): RasterSourceSpecification {
  return {
    type: "raster",
    tiles: [`pmtiles://${tileUrl}/{z}/{x}/{y}`],
    tileSize: 256,
    minzoom,
    maxzoom,
    bounds,
    attribution,
  };
}

function basemapLayers(p: Palette, fonts: FontStacks): LayerSpecification[] {
  const src = SOURCE_ID;

  return [
    {
      id: "landcover-wood",
      type: "fill",
      source: src,
      "source-layer": "landcover",
      filter: ["in", ["get", "class"], ["literal", ["wood", "forest"]]],
      paint: { "fill-color": p.wood },
    },
    {
      id: "landcover-grass",
      type: "fill",
      source: src,
      "source-layer": "landcover",
      filter: ["in", ["get", "class"], ["literal", ["grass", "farmland", "sand", "wetland"]]],
      paint: { "fill-color": p.grass },
    },
    {
      id: "landuse-residential",
      type: "fill",
      source: src,
      "source-layer": "landuse",
      filter: ["==", ["get", "class"], "residential"],
      minzoom: 9,
      paint: { "fill-color": p.landuseResidential },
    },
    {
      id: "landuse-industrial",
      type: "fill",
      source: src,
      "source-layer": "landuse",
      filter: ["in", ["get", "class"], ["literal", ["industrial", "commercial", "railway"]]],
      minzoom: 9,
      paint: { "fill-color": p.landuseIndustrial },
    },
    {
      id: "park",
      type: "fill",
      source: src,
      "source-layer": "park",
      paint: { "fill-color": p.wood, "fill-opacity": 0.7 },
    },
    {
      id: "water",
      type: "fill",
      source: src,
      "source-layer": "water",
      filter: ["!=", ["get", "intermittent"], 1],
      paint: { "fill-color": p.water },
    },
    {
      id: "waterway",
      type: "line",
      source: src,
      "source-layer": "waterway",
      minzoom: 9,
      paint: {
        "line-color": p.water,
        "line-width": ["interpolate", ["linear"], ["zoom"], 9, 0.6, 16, 3],
      },
    },
    {
      id: "building",
      type: "fill",
      source: src,
      "source-layer": "building",
      minzoom: 13,
      paint: {
        "fill-color": p.building,
        "fill-outline-color": p.buildingOutline,
        // Проявляем плавно, но уже к z14 наполовину: раньше на z14 они были
        // полностью прозрачными — как раз там, где на квартал смотрят впервые.
        "fill-opacity": ["interpolate", ["linear"], ["zoom"], 13.5, 0, 15, 1],
      },
    },
    {
      id: "rail",
      type: "line",
      source: src,
      "source-layer": "transportation",
      filter: ["==", ["get", "class"], "rail"],
      minzoom: 10,
      paint: {
        "line-color": p.rail,
        "line-width": ["interpolate", ["linear"], ["zoom"], 10, 0.5, 16, 2],
      },
    },
    {
      id: "road-casing",
      type: "line",
      source: src,
      "source-layer": "transportation",
      filter: [
        "in",
        ["get", "class"],
        ["literal", ["motorway", "trunk", "primary", "secondary", "tertiary"]],
      ],
      minzoom: 6,
      layout: { "line-cap": "round", "line-join": "round" },
      paint: {
        "line-color": p.roadCasing,
        "line-width": ["interpolate", ["exponential", 1.5], ["zoom"], 6, 1.6, 14, 8, 18, 26],
      },
    },
    // Второстепенные дороги тоже нуждаются в обводке: белая линия на светлом
    // фоне без неё попросту не видна, и карта выглядит пустым листом.
    {
      id: "road-minor-casing",
      type: "line",
      source: src,
      "source-layer": "transportation",
      filter: ["in", ["get", "class"], ["literal", ["minor", "service", "track"]]],
      minzoom: 12,
      layout: { "line-cap": "round", "line-join": "round" },
      paint: {
        "line-color": p.roadCasing,
        "line-width": ["interpolate", ["exponential", 1.5], ["zoom"], 12, 2, 18, 14],
      },
    },
    {
      id: "road-minor",
      type: "line",
      source: src,
      "source-layer": "transportation",
      filter: ["in", ["get", "class"], ["literal", ["minor", "service", "track"]]],
      minzoom: 12,
      layout: { "line-cap": "round", "line-join": "round" },
      paint: {
        "line-color": p.roadMinor,
        "line-width": ["interpolate", ["exponential", 1.5], ["zoom"], 12, 0.9, 18, 11],
      },
    },
    {
      id: "road-major",
      type: "line",
      source: src,
      "source-layer": "transportation",
      filter: [
        "in",
        ["get", "class"],
        ["literal", ["motorway", "trunk", "primary", "secondary", "tertiary"]],
      ],
      minzoom: 6,
      layout: { "line-cap": "round", "line-join": "round" },
      paint: {
        "line-color": p.roadMajor,
        "line-width": ["interpolate", ["exponential", 1.5], ["zoom"], 6, 0.8, 14, 5.5, 18, 20],
      },
    },
    {
      id: "boundary-admin",
      type: "line",
      source: src,
      "source-layer": "boundary",
      filter: ["<=", ["get", "admin_level"], 6],
      paint: {
        "line-color": p.boundary,
        "line-dasharray": [3, 2],
        "line-width": ["interpolate", ["linear"], ["zoom"], 4, 0.6, 12, 1.6],
      },
    },
    {
      id: "label-water",
      type: "symbol",
      source: src,
      "source-layer": "water_name",
      minzoom: 10,
      layout: {
        "text-field": ["coalesce", ["get", "name:ru"], ["get", "name"]],
        "text-font": fonts.regular,
        "text-size": 12,
        "text-max-width": 6,
      },
      paint: {
        "text-color": p.waterLabel,
        "text-halo-color": p.labelHalo,
        "text-halo-width": 1,
      },
    },
    {
      id: "label-road",
      type: "symbol",
      source: src,
      "source-layer": "transportation_name",
      minzoom: 13,
      layout: {
        "text-field": ["coalesce", ["get", "name:ru"], ["get", "name"]],
        "text-font": fonts.regular,
        "text-size": 11,
        "symbol-placement": "line",
      },
      paint: {
        "text-color": p.label,
        "text-halo-color": p.labelHalo,
        "text-halo-width": 1.4,
      },
    },
    {
      id: "label-place-minor",
      type: "symbol",
      source: src,
      "source-layer": "place",
      filter: [
        "in",
        ["get", "class"],
        ["literal", ["suburb", "neighbourhood", "village", "hamlet"]],
      ],
      minzoom: 11,
      layout: {
        "text-field": ["coalesce", ["get", "name:ru"], ["get", "name"]],
        "text-font": fonts.regular,
        "text-size": ["interpolate", ["linear"], ["zoom"], 11, 10, 16, 13],
        "text-max-width": 8,
      },
      paint: {
        "text-color": p.label,
        "text-halo-color": p.labelHalo,
        "text-halo-width": 1.4,
      },
    },
    {
      id: "label-place-major",
      type: "symbol",
      source: src,
      "source-layer": "place",
      filter: ["in", ["get", "class"], ["literal", ["city", "town"]]],
      layout: {
        "text-field": ["coalesce", ["get", "name:ru"], ["get", "name"]],
        "text-font": fonts.bold,
        "text-size": ["interpolate", ["linear"], ["zoom"], 6, 12, 14, 20],
        "text-max-width": 8,
      },
      paint: {
        "text-color": p.label,
        "text-halo-color": p.labelHalo,
        "text-halo-width": 1.6,
      },
    },
  ];
}

/**
 * Идентификатор слоя, ПЕРЕД которым вставляются тематические слои генплана.
 * Зоны должны лежать выше заливок и дорог, но ниже подписей — иначе названия
 * улиц утонут под полупрозрачными полигонами.
 */
export const THEMATIC_INSERT_BEFORE = "label-water";
